import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
  useTheme,
} from '@mui/material'
import { useApplyPatchVersion } from 'api/aws/deals/useApplyPatchVersion'
import { useSaveDealAndNavigate } from 'api/aws/deals/useSaveDealAndNavigate'
import ProductRowsTable from 'components/ProductRows/ProductRowsTable'
import { useEffect, useState } from 'react'
import type { NewDeal, OrganizationTableItem } from 'types'
import type { MasterAgreement, VersionedDeal } from 'utils/agreementVersioning'
import AmendmentSidebar from './AmendmentSidebar'
import { useAmendmentProducts } from './useAmendmentProducts'

// Patch (Change Order): price + margin + per-product dates editable.
// Margin was added when ops noted that mid-contract margin renegotiations
// (e.g. a client moving from 35% to 40% mgmt fee) should be expressible
// as a Change Order rather than forcing a full new contract. Heartbeat-
// side semantics for margin changes are a separate question — see the
// 06-16 review note in CHANGES.
const PATCH_EDITABLE_FIELDS = new Set(['price', 'margin', 'dateRange', 'startDate', 'endDate'])
// Minor mode: legacy — kept for any historical minor versions; same shape as before.
const MINOR_EDITABLE_FIELDS = new Set(['dateRange', 'startDate', 'endDate', 'qty', 'price', 'productName', 'selectedProduct'])
// Major mode: everything editable (add/remove products, change dates, pricing, margins)
const MAJOR_EDITABLE_FIELDS = new Set(['dateRange', 'startDate', 'endDate', 'qty', 'price', 'margin', 'productName', 'selectedProduct'])

interface VersionAmendmentDialogProps {
  open: boolean
  onClose: () => void
  currentVersion: VersionedDeal
  activeAgreement: MasterAgreement
  changeType: 'major' | 'minor' | 'patch'
  newDealBase: NewDeal
  nextVersion: string
  onSaveComplete?: () => void
}

const VersionAmendmentDialog = ({
  open,
  onClose,
  currentVersion,
  activeAgreement,
  changeType,
  newDealBase,
  nextVersion,
  onSaveComplete,
}: VersionAmendmentDialogProps) => {
  const theme = useTheme()
  const [isCreating, setIsCreating] = useState(false)
  const [isError, setIsError] = useState(false)
  const [needsNewContract, setNeedsNewContract] = useState(changeType === 'major')
  const [syncProductDates, setSyncProductDates] = useState(false)
  const [userChangedDates, setUserChangedDates] = useState(false)

  // Preserve the original deal dates — don't default to today
  const [dealStartDate, setDealStartDate] = useState((newDealBase.startDate as string) || '')
  const [dealEndDate, setDealEndDate] = useState((newDealBase.endDate as string) || '')
  const { handleSaveDealAndNavigate } = useSaveDealAndNavigate()
  const { mutateAsync: applyPatch } = useApplyPatchVersion()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')

  // For minor without new contract, keep product identity (PK/SK) for in-place update
  const keepIdentity = changeType === 'patch' || (changeType === 'minor' && !needsNewContract)

  const {
    draftProducts,
    handleProductChange,
    handleRemoveRow,
    handleAddProduct,
    handleDragEnd,
    syncAllProductDates,
    updateProductStartDates,
    currentTotals,
    newTotals,
    validationErrors,
    validateAll,
  } = useAmendmentProducts({
    sourceProducts: currentVersion.products,
    deal: newDealBase,
    changeType,
    keepIdentity,
    orgPK: currentVersion.deal.PK,
  })

  // Only sync product dates when the user explicitly changes the deal dates or toggles the switch.
  // Skip on initial mount to preserve per-product dates from the source deal.
  useEffect(() => {
    if (changeType === 'patch') return
    if (!userChangedDates) return
    if (syncProductDates && dealStartDate && dealEndDate) {
      syncAllProductDates(dealStartDate, dealEndDate)
    } else if (!syncProductDates && dealStartDate) {
      updateProductStartDates(dealStartDate)
    }
  }, [changeType, userChangedDates, syncProductDates, dealStartDate, dealEndDate, syncAllProductDates, updateProductStartDates])

  // Build a fake deal object for ProductRowsTable (it needs isDraft and type)
  const draftDeal = {
    ...newDealBase,
    isDraft: true,
    type: newDealBase.type || currentVersion.deal.type,
  }

  const editableFields =
    changeType === 'patch'
      ? PATCH_EDITABLE_FIELDS
      : changeType === 'minor'
        ? MINOR_EDITABLE_FIELDS
        : changeType === 'major'
          ? MAJOR_EDITABLE_FIELDS
          : undefined

  const handleSave = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!validateAll()) return

    if (changeType === 'patch') {
      // --- PATCH (Change Order): in-place update for pricing and/or dates ---
      // The patch endpoint already accepts `dealDates`; the old UI only fed
      // pricing because date editing was minor-only. Now every change type
      // can adjust dates, so we always forward dealStartDate / dealEndDate
      // when at least one is set.
      setIsCreating(true)
      try {
        await applyPatch({
          PK: currentVersion.deal.PK,
          SK: currentVersion.deal.SK,
          deal_uuid: currentVersion.deal.deal_uuid,
          newSemanticVersion: nextVersion,
          products: draftProducts,
          currentUser: currentUser as any,
          changeType: 'patch',
          ...(dealStartDate || dealEndDate
            ? { dealDates: { startDate: dealStartDate, endDate: dealEndDate } }
            : {}),
        })
        onClose()
        onSaveComplete?.()
      } catch {
        setIsError(true)
      } finally {
        setIsCreating(false)
      }
    } else if (changeType === 'minor' && !needsNewContract) {
      // --- MINOR without new contract: in-place update (dates + pricing) ---
      setIsCreating(true)
      try {
        await applyPatch({
          PK: currentVersion.deal.PK,
          SK: currentVersion.deal.SK,
          deal_uuid: currentVersion.deal.deal_uuid,
          newSemanticVersion: nextVersion,
          products: draftProducts,
          currentUser: currentUser as any,
          changeType: 'minor',
          dealDates: {
            startDate: dealStartDate,
            endDate: dealEndDate,
          },
        })
        onClose()
        onSaveComplete?.()
      } catch {
        setIsError(true)
      } finally {
        setIsCreating(false)
      }
    } else {
      // --- MAJOR or MINOR with new contract: create a new deal (no contractId → PandaDoc signing) ---
      const maId = activeAgreement.masterAgreementId || undefined // Don't pass empty string — DynamoDB GSI rejects it
      const finalDeal = {
        ...newDealBase,
        startDate: dealStartDate,
        endDate: dealEndDate,
        semanticVersion: nextVersion || undefined,
        masterAgreementId: maId,
        versionChangeType: changeType,
        versionType: changeType,
        contractId: undefined, // Force new contract — user goes through PandaDoc signing
      }

      // Strip remaining identity fields so backend creates new products
      const productsToSave = draftProducts.map(p => ({
        ...p,
        PK: undefined,
        deal_uuid: undefined,
        createdAt: undefined,
      }))

      handleSaveDealAndNavigate({
        event,
        newDeal: (finalDeal as unknown) as OrganizationTableItem,
        assignedProducts: productsToSave as any,
        setNewDeal: (() => {}) as any,
        setIsCreating,
        resetDealState: () => {},
        onClose: () => {
          onClose()
          onSaveComplete?.()
        },
        setIsError,
        validateFields: () => true,
        cloneMode: 'version',
      })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={changeType === 'major' ? 'Major' : changeType === 'minor' ? 'Minor' : 'Patch'}
            size="small"
            color={changeType === 'major' ? 'error' : changeType === 'minor' ? 'info' : 'warning'}
            sx={{ fontWeight: 700, fontSize: 11, height: 22 }}
          />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {changeType === 'major'
              ? 'Add/Remove Products'
              : changeType === 'minor'
                ? 'Date Renewal' // legacy — kept for historical minor versions only
                : 'Change Order'}
          </Typography>
          <Chip
            label={`v${nextVersion}`}
            size="small"
            color="primary"
            sx={{ fontFamily: 'monospace', fontWeight: 700, height: 22, fontSize: 11 }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {currentVersion.deal.name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', gap: 2, overflow: 'hidden', pt: 1 }}>
        {/* Minor-specific toggles */}
        {/* {changeType === 'minor' && (
          <Box sx={{ position: 'absolute', top: 5, right: 214, display: 'flex', flexDirection: 'column', gap: 0 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={needsNewContract}
                  onChange={(_, checked) => setNeedsNewContract(checked)}
                  disabled={changeType === 'major'}
                />
              }
              label={<Typography variant="caption">Requires new contract</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={syncProductDates}
                  onChange={(_, checked) => { setUserChangedDates(true); setSyncProductDates(checked) }}
                />
              }
              label={<Typography variant="caption">Sync product dates to deal dates</Typography>}
            />
          </Box>
        )} */}
        {/* Left: Product Table */}
        <Box sx={{ flex: 3, overflow: 'auto', minWidth: 0 }}>
          <ProductRowsTable
            deal={draftDeal}
            productRows={draftProducts}
            onDragEnd={handleDragEnd}
            onProductChange={handleProductChange}
            validationErrors={validationErrors}
            removeRow={handleRemoveRow}
            editableFields={editableFields}
          />

          {/* Add Service button — for minor and major changes */}
          {(changeType === 'minor' || changeType === 'major') && (
            <Box sx={{ mt: 1, px: 1 }}>
              <Button
                sx={{ '&:hover': { opacity: '80%' } }}
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddProduct}
              >
                Add Service
              </Button>
            </Box>
          )}

          {validationErrors.products && (
            <Typography sx={{ color: 'error.main', fontSize: 12, mt: 1, px: 1 }}>
              {draftProducts.length === 0
                ? 'At least one product is required'
                : 'All products must have a name selected'}
            </Typography>
          )}
          {validationErrors.dates && (
            <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5, px: 1 }}>
              One or more products have an end date before the start date — please update the dates.
            </Typography>
          )}
        </Box>

        {/* Right: Sidebar */}
        <Box sx={{ flex: 1, minWidth: 260, maxWidth: 320, overflow: 'auto' }}>
          <AmendmentSidebar
            currentVersion={currentVersion}
            currentTotals={currentTotals}
            newTotals={newTotals}
            nextVersion={nextVersion}
            changeType={changeType}
            dealStartDate={dealStartDate}
            dealEndDate={dealEndDate}
            onStartDateChange={(date: string) => { setUserChangedDates(true); setDealStartDate(date) }}
            onEndDateChange={(date: string) => { setUserChangedDates(true); setDealEndDate(date) }}
          />
          {(changeType === 'minor' || changeType === 'major') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={needsNewContract}
                    onChange={(_, checked) => setNeedsNewContract(checked)}
                    disabled={changeType === 'major'}
                  />
                }
                label={<Typography variant="caption">Requires new contract</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={syncProductDates}
                    onChange={(_, checked) => {
                      // The sync effect is gated on userChangedDates so it doesn't
                      // overwrite per-product dates on initial mount. Toggling the
                      // switch is itself an explicit user-driven date change.
                      setUserChangedDates(true)
                      setSyncProductDates(checked)
                    }}
                  />
                }
                label={<Typography variant="caption">Sync product dates to deal dates</Typography>}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            backgroundColor: 'rgb(244, 67, 54, 0.2)',
            '&:hover': { backgroundColor: 'rgb(244, 67, 54, 0.7)' },
          }}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="secondary" disabled={isCreating}>
          {isCreating
            ? changeType === 'patch'
              ? 'Applying...'
              : changeType === 'minor' && !needsNewContract
              ? 'Applying...'
              : 'Creating...'
            : changeType === 'patch'
            ? 'Apply Patch'
            : changeType === 'minor' && !needsNewContract
            ? 'Apply Renewal'
            : 'Create Version'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default VersionAmendmentDialog
